import { useValue } from "@tldraw/state-react";
import type { Editor } from "./canvas";
import { getLineGuide } from "./line-guide-state";

/** Local overlay showing the actual aligned line; never saved on the board. */
export function LineAlignmentGuide({ editor }: { editor: Editor }) {
  const guide = useValue(
    "line alignment overlay",
    () => {
      if (!editor.isIn("line") && !editor.isIn("select.dragging_handle"))
        return null;
      const alignment = getLineGuide(editor).get();
      return alignment
        ? {
            ...alignment,
            start: editor.pageToScreen(alignment.start),
            end: editor.pageToScreen(alignment.end),
            viewport: editor.getViewportScreenBounds(),
            editing: editor.isIn("select.dragging_handle"),
          }
        : null;
    },
    [editor],
  );
  if (!guide) return null;
  const { start, end, viewport, locked } = guide;
  const length = Math.hypot(end.x - start.x, end.y - start.y) || 1;
  const dx = ((end.x - start.x) / length) * 24;
  const dy = ((end.y - start.y) / length) * 24;
  const label = guide.axis === "horizontal" ? "Horizontal" : "Vertical";
  const mac = /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <div className="line-alignment-guide" data-locked={locked}>
      <svg className="line-alignment-axis" aria-hidden="true">
        <line
          x1={start.x - dx}
          y1={start.y - dy}
          x2={end.x + dx}
          y2={end.y + dy}
        />
        <circle cx={end.x} cy={end.y} r={4} />
      </svg>
      <div
        className="line-alignment-hint"
        style={{
          left: Math.max(
            viewport.x + 128,
            Math.min(viewport.maxX - 128, (start.x + end.x) / 2),
          ),
          top: Math.max(
            viewport.y + 8,
            Math.min(
              viewport.maxY - 40,
              (start.y + end.y) / 2 + (guide.editing ? 18 : -44),
            ),
          ),
        }}
      >
        <svg
          className="line-alignment-icon"
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <path
            d="M2 8h12M2 5v6M14 5v6"
            transform={guide.axis === "vertical" ? "rotate(90 8 8)" : undefined}
          />
        </svg>
        <strong>{label}</strong>
        <span className="line-alignment-shortcut">
          <kbd aria-label={locked ? "Shift" : mac ? "Option" : "Alt"}>
            {locked ? "⇧" : mac ? "⌥" : "Alt"}
          </kbd>
          <span>{locked ? "Locked" : "Free draw"}</span>
        </span>
      </div>
    </div>
  );
}
